
<script setup>

import ItemThumbnail from "@/Components/ItemThumbnail.vue";
import IllustrationFailedComponent from "@/Components/IllustrationFailedComponent.vue";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import {computed, reactive, ref} from "vue";
import InputFieldPrompt from "@/Components/InputFieldPrompt.vue";
import VoiceDetail from "@/Components/VoiceDetail.vue";
import VoiceSelectionModal from "@/Components/Voice/VoiceSelectionModal.vue";
import _ from "lodash";

const emits = defineEmits(['image-selected', 'npc-updated', 'display-regenerate-illustration', 'generate-voice', 'confirm-voice']);

const props = defineProps({
    npc: {
        type: Object,
        default: null,
        nullable: true,
    },
    isEditing: {
        type: Boolean,
        default: false,
    },
    isAuthor: {
        type: Boolean,
        default: false,
    },
    showVoiceSection: {
        type: Boolean,
        default: false,
    },
    isGeneratingVoice: {
        type: Boolean,
        default: false,
    },
    isConfirmingVoice: {
        type: Boolean,
        default: false,
    },
});

const data = reactive({
    selectedPropertyKey: null,
    selectedPropertyValue: null,
    selectedPropertyIndex: null,
    selectedValidationType: 'text',
    selectedMinValue: null,
    selectedMaxValue: null,
})

const showVoiceModal = ref(false)

const hasConfirmedVoice = computed(() => {
    if (!props.npc?.voices) return false
    return props.npc.voices.some(v => v.selected_at !== null)
})

function openVoiceModal() {
    showVoiceModal.value = true
}

function closeVoiceModal() {
    showVoiceModal.value = false
}

function npcUpdated(data) {
    emits('npc-updated', data)
}

function editProperty(key, value, index = null, validationType = 'text', minValue = null, maxValue = null) {
    data.selectedPropertyKey = key;
    data.selectedPropertyValue = value;
    data.selectedPropertyIndex = index;
    data.selectedValidationType = validationType;
    data.selectedMinValue = minValue;
    data.selectedMaxValue = maxValue;
}

function imageSelected(image) {
    emits('image-selected', image);
}

function handleRegenerateIllustration(data) {
    // Emit event to parent component to display regeneration form
    emits('display-regenerate-illustration');
}

function prettyKey(key) {
    const prettyKey = key.replace(/_/g, ' ');
    return _.startCase(prettyKey);
}

function displaySign(value) {
    if (value === undefined || value === null) {
        return "";
    }

    if (typeof value !== 'string') {
        value = value.toString();
    }

    if (value.charAt(0) !== '+' && value.charAt(0) !== '-') {
        return value > 0 ? `+${value}` : `${value}`;
    }
    return value
}

const attributesCategory = computed(() => {
    return props.npc.attributes ? Object.fromEntries(
        Object.entries(props.npc.attributes).filter(([key, value]) => value !== null)
    ) : {};
});

const actions = computed(() => {
    return props.npc.actions?.filter(action => action.name !== "" && action.description !== "");
});

const illustrationFailed = computed(() => {
    return props.npc?.metadata?.illustration_failed === true;
});

function getNumericFieldConfig(key) {
    const numericFields = {
        // Ability Scores
        'str': { min: 1, max: 30 },
        'dex': { min: 1, max: 30 },
        'con': { min: 1, max: 30 },
        'int': { min: 1, max: 30 },
        'wis': { min: 1, max: 30 },
        'cha': { min: 1, max: 30 },

        // Combat Stats
        'hit_points': { min: 1, max: 999 },
        'armor_class': { min: 1, max: 30 },
    };

    return numericFields[key] || null;
}

</script>

<template>
    <div class="bg-gray-900 text-white">

        <InputFieldPrompt
            v-if="data.selectedPropertyKey && props.isAuthor"
            :property-key="data.selectedPropertyKey"
            :property-value="data.selectedPropertyValue"
            :property-index="data.selectedPropertyIndex"
            :validation-type="data.selectedValidationType"
            :min-value="data.selectedMinValue"
            :max-value="data.selectedMaxValue"
            @close="data.selectedPropertyKey = null"
            @updated="npcUpdated($event)"
        />

        <div class="mx-auto py-8 px-4 sm:py-8 sm:px-6 lg:max-w-7xl lg:px-8">
            <div class="lg:grid lg:grid-cols-7 lg:grid-rows-1 lg:gap-x-8 lg:gap-y-10 xl:gap-x-12">
                <div class="lg:col-span-3 lg:row-end-2">
                    <div class="aspect-w-4 aspect-h-4 overflow-hidden rounded-lg mx-auto max-w-2xl">
                        <ItemThumbnail v-if="npc && !illustrationFailed"
                                       :height="'26rem'"
                                       :width="'26rem'"
                                       :image-url="npc.illustrations[0]?.url"
                                       :image-description="npc.image_description"
                                       @image-selected="imageSelected($event)"
                        />
                        <IllustrationFailedComponent
                            v-else-if="npc && illustrationFailed"
                            content-type="npc"
                            :content-id="npc.id"
                            @regenerate-illustration="handleRegenerateIllustration"
                        />
                    </div>
                </div>

                <!-- Merchant Information -->
                <div class="mx-auto mt-14 max-w-2xl sm:mt-16 lg:col-span-3 lg:row-span-2 lg:row-end-2 lg:mt-0 lg:max-w-none">
                    <div class="flex flex-col-reverse">
                        <div class="mt-8">
                            <div class="flex">
                                <FontAwesomeIcon v-if="props.isEditing" :icon="['fas', 'pencil']" class="mt-2 mr-2" size="sm" style="cursor: pointer" @click="editProperty('name', npc.name)"/>
                                <h1 class="text-2xl font-bold tracking-tight">{{ npc.name }}</h1>
                            </div>

                            <div class="mt-2 text-sm">
                                <p v-if="!props.isEditing">
                                    Race: {{ npc.race }}
                                </p>
                                <p v-if="!props.isEditing && npc.main.hit_points">
                                    Hit Points: {{ npc.main.hit_points }}
                                </p>
                                <div class="flex" v-if="props.isEditing">
                                    <FontAwesomeIcon :icon="['fas', 'pencil']" class="mt-2 mr-2" size="2xs" style="cursor: pointer" @click="editProperty('race', npc.race)"/>
                                    <h1>Race: {{ npc.race }}</h1>
                                </div>
                                <div class="flex" v-if="props.isEditing">
                                    <FontAwesomeIcon :icon="['fas', 'pencil']" class="mt-2 mr-2" size="2xs" style="cursor: pointer" @click="editProperty('hit_points', npc.main.hit_points, null, 'number', 1, 999)"/>
                                    <h1>Hit Points: {{ npc.main.hit_points }}</h1>
                                </div>
                                <div class="mt-1">
                                    <VoiceDetail
                                        :voices="npc?.voices"
                                        :is-editing="props.isEditing"
                                        :show-edit-icon="props.showVoiceSection && props.isAuthor"
                                        @edit-voice="openVoiceModal"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="flex mt-4">
                        <FontAwesomeIcon v-if="props.isEditing" :icon="['fas', 'pencil']" class="mt-1.5 mr-2" size="sm" style="cursor: pointer;" @click="editProperty('introduction_dialogue', npc.introduction_dialogue)"/>
                        <p>{{ npc.introduction_dialogue }}</p>
                    </div>

                    <div class="mt-4 border-t border-white pt-4">
                        <div class="prose prose-sm ">
                            <div class="flex">
                                <FontAwesomeIcon v-if="props.isEditing" :icon="['fas', 'pencil']" class="mt-1.5 mr-2" size="sm" style="cursor: pointer;" @click="editProperty('backstory', npc.backstory)"/>
                                <p>{{ npc.backstory }}</p>
                            </div>
                        </div>
                    </div>
                </div>

<!--                <div class="mx-auto mt-14 max-w-2xl sm:mt-16 lg:col-span-3 lg:mt-0 lg:max-w-none">-->
<!--                    <div class="mt-4 border-t border-white pt-4">-->
<!--                        <h3 class="text-sm font-medium">Appearance</h3>-->
<!--                        <div class="prose prose-sm mt-4">-->
<!--                            <div class="flex">-->
<!--                                <FontAwesomeIcon v-if="props.isEditing" :icon="['fas', 'pencil']" class="mt-1.5 mr-2" size="sm" style="cursor: pointer;" @click="editProperty('image_description', npc.image_description)"/>-->
<!--                                <p>{{ npc.image_description }}</p>-->
<!--                            </div>-->
<!--                        </div>-->
<!--                    </div>-->
<!--                </div>-->
            </div>

            <!-- Attributes -->
            <div v-if="Object.keys(attributesCategory).length > 0" class="mx-auto mt-2 max-w-2xl w-full lg:col-span-3 lg:max-w-none">
                <div class="mt-4 border-t border-white pt-4 text-lg mb-6 sm:mb-8">
                    <ul role="list">
                        <div class="mt-2 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-6">
                            <li v-for="(value, key, index) in attributesCategory" :key="index" class="flex flex-col items-start w-30">
                                <div v-if="!props.isEditing" class="flex flex-col items-center w-24">
                                    <strong class="text-xl hidden sm:block -ml-2">{{ prettyKey(key) }}</strong>
                                    <div class="relative w-14 hidden sm:block">
                                        <FontAwesomeIcon :icon="['far', 'shield']" class="mt-1 mr-2 fa-3x cursor-pointer" @click="editProperty(key, value)"/>
                                        <span class="absolute text-2xl text-center w-6" style="top: 50%; left: 0%; transform: translate(52%, -50%);">{{ value }}</span>
                                    </div>
                                </div>
                                <div v-if="!props.isEditing" class="flex flex-col block sm:hidden">
                                    <strong class="text-base ">{{ prettyKey(key) }}: {{value}}</strong>
                                </div>
                                <div v-if="props.isEditing" class="flex flex-col">
                                    <div class="flex">
                                        <FontAwesomeIcon v-if="props.isEditing" :icon="['fas', 'pencil']" class="mt-1 mr-2" size="sm" style="cursor: pointer" @click="getNumericFieldConfig(key) ? editProperty(key, value, null, 'number', getNumericFieldConfig(key).min, getNumericFieldConfig(key).max) : editProperty(key, value)"/>
                                        <strong class="text-xl ">{{ prettyKey(key) }}: {{ value }}</strong>
                                    </div>
                                </div>
                            </li>
                        </div>
                    </ul>
                </div>

                <!-- Actions -->
                <div v-if="actions && actions.length > 0" class="mt-4 border-t border-white pt-4 text-lg mb-6 sm:mb-12">
                    <ul role="list">
                        <li v-for="(value, index) in actions" :key="index">
                            <div class="flex mt-2">
                                <FontAwesomeIcon v-if="props.isEditing" :icon="['fas', 'pencil']" class="mt-1 mr-2" size="sm" style="cursor: pointer" @click="editProperty(`actions_name`, value.name, index)"/>
                                <p class="text-base font-bold">
                                    {{ value.name }}
                                    <template v-if="value.damage_dice">
                                        -
                                        <FontAwesomeIcon v-if="props.isEditing" :icon="['fas', 'pencil']" class="mt-1 mr-2" size="sm" style="cursor: pointer" @click="editProperty(`actions_damage`, value.damage_dice, index, 'dice')"/>
                                        <span class="text-red-500">{{ value.damage_dice }}</span>
                                    </template>
                                    <template v-if="value.armor_class">
                                        <FontAwesomeIcon v-if="props.isEditing" :icon="['fas', 'pencil']" class="mt-1 mr-2" size="sm" style="cursor: pointer" @click="editProperty(`actions_armor`, value.armor_class, index, 'number', 1, 30)"/>
                                        <span class="text-blue-500">AC: {{ value.armor_class }}</span>
                                    </template>
                                </p>
                            </div>
                            <div class="flex">
                                <FontAwesomeIcon v-if="props.isEditing" :icon="['fas', 'pencil']" class="mt-1 mr-2" size="sm" style="cursor: pointer" @click="editProperty(`actions_description`, value.description, index)"/>
                                <p class="text-sm text-gray-400">
                                    {{ value.description }}
                                </p>
                            </div>
                        </li>
                    </ul>
                </div>

                <!-- Voice Description -->
                <div v-if="props.showVoiceSection && (npc.voice_description || props.isEditing)" class="mt-4 border-t border-white pt-4 text-lg">
                    <div class="prose prose-sm">
                        <p class="font-bold">Voice Description</p>
                        <div class="flex text-gray-300">
                            <FontAwesomeIcon v-if="props.isEditing && props.isAuthor" :icon="['fas', 'pencil']" class="mt-1 mr-2" size="sm" style="cursor: pointer" @click="editProperty('voice_description', npc.voice_description || '')"/>
                            <p v-if="npc.voice_description">{{ npc.voice_description }}</p>
                            <p v-else-if="props.isEditing" class="text-gray-500 italic">Click pencil to add a voice description...</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Voice Selection Modal -->
        <VoiceSelectionModal
            :show="showVoiceModal"
            :voices="npc?.voices || []"
            :is-generating="isGeneratingVoice"
            :is-confirming="isConfirmingVoice"
            :has-confirmed-voice="hasConfirmedVoice"
            @close="closeVoiceModal"
            @generate-voice="emits('generate-voice')"
            @confirm-voice="emits('confirm-voice', $event)"
        />
    </div>
</template>
